// reportCaptureUtils.js

import EdgeOrREST from '../EdgeOrREST';

/**
 * Handles the logic for uploading captured report areas.
 * @param {object} response - The API response object.
 * @param {function} setSnack - Function to set notification snackbar state.
 */
export const onUploadReportCapture = (response, setSnack) => {
    if (response && response.status === '200') {
        console.log("Upload Report Capture Response", response);
        const link = document.createElement('a');
        link.download = 'report.pdf';
        link.href = response.data.data;
        link.click();
        setSnack({
            open: true,
            message: 'Report capture success',
            severity: 'success'
        });
    } else {
        setSnack({
            open: true,
            message: "Report capture failed",
            severity: 'error'
        });
    }
};

/**
 * Captures the report areas from the Konva stage.
 * @param {object} params - Object containing all necessary parameters.
 * @param {object} params.stageRef - React ref to the Konva Stage.
 * @param {Array} params.reportCaptureAreas - Array of report capture area objects.
 * @param {function} params.setReportCaptureAreas - State setter for reportCaptureAreas.
 * @param {Array} params.masks - Array of mask objects.
 * @param {function} params.setMasks - State setter for masks.
 * @param {number} params.realWorldWidth - Real-world width.
 * @param {number} params.imagePixelWidth - Image pixel width.
 * @param {number} params.realWorldHeight - Real-world height.
 * @param {number} params.imagePixelHeight - Image pixel height.
 * @param {function} params.setSnack - Function to set notification snackbar state.
 * @param {Array} originalStates - The original opacity states of report capture areas before temporary change.
 * @param {Array|null} originalMasks - The original opacity states of masks (if masks were hidden).
 */
export const captureReportAreas = (params, originalStates, originalMasks) => {
    const {
        stageRef,
        reportCaptureAreas,
        setReportCaptureAreas,
        masks,
        setMasks,
        realWorldWidth,
        imagePixelWidth,
        realWorldHeight,
        imagePixelHeight,
        setSnack,
    } = params;

    const stage = stageRef.current;
    if (!stage) return; // Ensure stageRef.current exists

    const originalScale = stage.scaleX();
    const originalPosition = { x: stage.x(), y: stage.y() };
    const originalSize = { width: stage.width(), height: stage.height() };

    const temp = [];
    reportCaptureAreas.forEach((item) => { // Use forEach as we're not returning a new array for this map
        const itemBoundingBox = { width: item.width, height: item.height, scaleX: item.scaleX, scaleY: item.scaleY, rotation: item.rotation, points: item.points };

        // Calculate actual pixel dimensions and position based on image scaling
        const pixelWidthRatio = imagePixelWidth / realWorldWidth;
        const pixelHeightRatio = imagePixelHeight / realWorldHeight;

        // Set the stage dimensions to match the item bounding box
        stage.size({
            width: (itemBoundingBox.width * itemBoundingBox.scaleX) * pixelWidthRatio * originalScale, // Use originalScale here
            height: (itemBoundingBox.height * itemBoundingBox.scaleY) * pixelHeightRatio * originalScale, // Use originalScale here
        });

        // Adjust the stage position to align with the item bounding box
        stage.position({
            x: -itemBoundingBox.points[0].x * pixelWidthRatio * originalScale,
            y: -itemBoundingBox.points[0].y * pixelHeightRatio * originalScale,
        });

        temp.push(stage.toDataURL({ pixelRatio: 2 }));
    });

    EdgeOrREST(null, 'UPLOAD_REPORT_CAPTURE', (response) => onUploadReportCapture(response, setSnack), { data: temp });

    // Restore the original stage settings
    stage.size(originalSize);
    stage.scale({ x: originalScale, y: originalScale });
    stage.position(originalPosition);

    // Restore the original opacity states for report areas
    setReportCaptureAreas(originalStates.map(state => {
        const item = reportCaptureAreas.find(area => area.id === state.id);
        return item ? { ...item, opacity: state.opacity } : null;
    }).filter(Boolean)); // Filter out nulls if an item was not found

    // Restore the original opacity states for masks if they were altered
    if (originalMasks != null) {
        setMasks(originalMasks.map(state => {
            const item = masks.find(mask => mask.id === state.id);
            return item ? { ...item, opacity: state.opacity } : null;
        }).filter(Boolean)); // Filter out nulls
    }
};

/**
 * Initiates the report capture process, temporarily hiding masks/areas for capture.
 * @param {boolean} maskOn - True if masks should be included in the report, false otherwise.
 * @param {object} params - Object containing all necessary parameters.
 * @param {object} params.stageRef - React ref to the Konva Stage.
 * @param {Array} params.reportCaptureAreas - Array of report capture area objects.
 * @param {function} params.setReportCaptureAreas - State setter for reportCaptureAreas.
 * @param {Array} params.masks - Array of mask objects.
 * @param {function} params.setMasks - State setter for masks.
 * @param {number} params.realWorldWidth - Real-world width.
 * @param {number} params.imagePixelWidth - Image pixel width.
 * @param {number} params.realWorldHeight - Real-world height.
 * @param {number} params.imagePixelHeight - Image pixel height.
 * @param {function} params.setSnack - Function to set notification snackbar state.
 */
export const handleReportCaptureClick = (maskOn, params) => {
    const {
        stageRef,
        reportCaptureAreas,
        setReportCaptureAreas,
        masks,
        setMasks,
        realWorldWidth,
        imagePixelWidth,
        realWorldHeight,
        imagePixelHeight,
        setSnack,
    } = params;

    if (stageRef.current) {
        // Temporarily hide report capture areas
        const tempReportCaptureAreas = reportCaptureAreas.map(item => ({
            ...item,
            opacity: 0,
            selected: false,
        }));
        const originalReportAreaStates = reportCaptureAreas.map(item => ({
            id: item.id,
            opacity: item.opacity,
        }));
        setReportCaptureAreas(tempReportCaptureAreas);

        let originalMasks = null;
        if (!maskOn) {
            // If masks are not to be included, temporarily hide them too
            originalMasks = masks.map(item => ({
                id: item.id,
                opacity: item.opacity,
            }));
            const tempMasks = masks.map(item => ({
                ...item,
                opacity: 0,
                selected: false,
            }));
            setMasks(tempMasks);
        } else {
            // If masks are included, just deselect them
            const tempMasks = masks.map(item => ({
                ...item,
                selected: false,
            }));
            setMasks(tempMasks);
        }

        // Delay the capture to allow Konva to re-render with updated opacities
        setTimeout(() => {
            captureReportAreas(
                {
                    stageRef,
                    reportCaptureAreas,
                    setReportCaptureAreas,
                    masks,
                    setMasks,
                    realWorldWidth,
                    imagePixelWidth,
                    realWorldHeight,
                    imagePixelHeight,
                    setSnack,
                },
                originalReportAreaStates,
                originalMasks
            );
        }, 0); // Using setTimeout with 0 allows the render cycle to complete
    }
};